import React, {useState} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../../components/content-box/ContentBox";
import PageActions from "../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../components/breadcrumbs/Breadcrumbs";
import Modal from "../../../../../../components/modal/Modal";
import _image_service from "../../../../../../services/ImageService";
import '../../../../../styles/Common.scss';
import '../../WorldPages.scss';
import '../../WorldDynamicContent.scss';

interface DraconicHistoryPageProps {
}

const DraconicHistoryPage = (props: DraconicHistoryPageProps) => {

    const _map_src = `chase/world/geography/casarok_pre_cataclysm.png`;

    const page: PageActions = new PageActions({
        title: `Casarok History: Draconic Age - Chase the Fox`,
        description: "The Draconic Age of Casarok covers the time when the intelligent life, the Draconic race, emerged in the world!",
        keywords: ["casarok", "casarok history", "draconic age", "fantasy timeline", "fantasy world history", "fantasy barbarians", "barbarian dragons", "fantasy stone age"],
        imgSrc: _map_src,
        url: `world/history/draconic-age`
    });

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    return page.generateMetaContent(
        <>
            <Modal isOpen={modalIsOpen}
                   onClose={() => setModalIsOpen(false)}>
                <div className="geography-modal-view">
                    <p className="center">
                        <a href={_image_service.getCdnImageSrc(_map_src, 'full')}
                           target="_blank"
                           rel="noopener noreferrer">
                            Click here to view the full size image.
                        </a>
                    </p>
                    <img src={_image_service.getCdnImageSrc(_map_src, 'desktop')}
                         title='Casarok Pre-Cataclysm Map'
                         alt="A map of Casarok before the Great Cataclysm"/>
                </div>
            </Modal>
            <ContentBox header="The Draconic Age of Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <a id="visible-element"
                       className="switch-visible geography-modal-link"
                       onClick={() => setModalIsOpen(true)}>
                        <img className="block width-50-percent"
                             src={_image_service.getCdnImageSrc(`chase/world/geography/casarok_pre_cataclysm.png`)}
                             title='Casarok Pre-Cataclysm Map'
                             alt="A map of Casarok before the Great Cataclysm"/>
                    </a>
                </div>
                <div className="world-page-static">
                    <p className="center">
                        <em>This age covers the years of roughly 10,000 - 5,000 BCE.</em>
                    </p>
                    <p>
                        The Draconic Age of Casarok encompasses the time that
                        the <NavLink className="world-link" to="/world/bestiary/draconic">Draconic</NavLink> race was
                        the only intelligent and dominant life in the world. Each species in this race evolved from
                        primordial dragons placed on Casarok by
                        the <NavLink className="world-link" to="/world/religion/pantheon">Dragon Deities</NavLink>.
                        Each of these species were blessed by various gods and goddesses to take on certain elements and
                        abilities that they would carry with them and pass on to their descendants indefinitely. These
                        elemental powers caused many intraracial problems, forcing the world to be split across the
                        elements they embodied. Life was extremely primitive, using only wooden and stone tools up
                        until bronze was beginning to be forged late in this time period.
                    </p>
                    <p>
                        Primordial animals and dragons existed in this time as well. They were often the targets of
                        Draconic hunters during this time, specially the larger ones that provided the most meat. Since
                        most Draconic species are also herbivores or petravores (those who can eat rocks for their
                        minerals), hunting was only used as a source of protein needed to become stronger and fend off
                        opposing clans or species. It became particularly important as one species threatened the
                        rest and could potentially destroy the world with their power.
                    </p>
                    <p>
                        <em>How does Casarok's Draconic Age differ from the stone age on Earth?</em><br/>
                        This age mirrors Earth's stone age fairly closely with the Draconic race replacing humans as
                        the dominant race of the planet. Primitive polytheistic religions toward the gods and goddesses
                        that empowered each species replace those from Earth. Bronze Age tools were being developed
                        late in this age, particularly by those with the innate power to forge them.
                    </p>
                    <h3 className="headline">Notable Events (in order of their occurrence)</h3>
                    <ul className="spaced">
                        <li>
                            The first intelligent dragon species emerge on the planet.
                        </li>
                        <li>
                            The gods and goddesses bless each of them with various powers and abilities that they would
                            carry on throughout time.
                        </li>
                        <li>
                            Fire is first used by
                            the <NavLink className="world-link" to="/world/bestiary/draconic/dracamma">dracammas</NavLink>.
                        </li>
                        <li>
                            Several new inventions, such as the wheel, are created by
                            the <NavLink className="world-link" to="/world/bestiary/draconic/dracamma">drekaldar</NavLink>.
                        </li>
                        <li>
                            Wars and territorial struggles break out among the various species.
                        </li>
                        <li>
                            The darcanissae, magic dragons, gain the ability to siphon the powers of other Draconics.
                            They use the wars of their Draconic brethren to begin conquering them all.
                        </li>
                        <li>
                            The other Draconic species set aside their differences to take on the darcaniss threat.
                        </li>
                        <li>
                            The darcanissae begin to siphon magic from the world itself to power their ambitions.
                        </li>
                        <li>
                            The other Draconic species defeat the darcanissae. The darcanissae essentially go extinct,
                            or their presence is wiped from history.
                        </li>
                        <li>
                            Fight and territorial disputes continue with the dracammas becoming the most dominant in
                            the world.
                        </li>
                        <li>
                            Several Draconic species begin forging bronze to form tools and weapons.
                        </li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default DraconicHistoryPage;